import { Component, OnInit, input, inject, signal, computed } from '@angular/core';
import * as Models from '../models';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatSelectModule } from '@angular/material/select';
import { LocaleNamePipe } from '../locale-name-pipe';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ImageFallbackDirective } from '../img-fallback.directive';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { ReportDomainService } from '../domain-services/report-domain.service';
import { PackageDomainService } from '../domain-services/package-domain.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { ACTIVE_CLIENT } from '../../main';

@Component({
  selector: 'app-edit-report-settings',
  imports: [
    MatButtonModule,
    RouterLink,
    MatSelectModule,
    LocaleNamePipe,
    MatFormFieldModule,
    ImageFallbackDirective,
  ],
  templateUrl: './edit-report-settings.html',
  styleUrl: './edit-report-settings.scss',
})
export class EditReportSettings implements OnInit {
  report = input.required<Models.Report | Models.Package>();
  languages = input.required<Models.Languages>();
  norms = input.required<Models.Norms>();

  private readonly projectDomain = inject(ProjectDomainService);
  private readonly reportDomain = inject(ReportDomainService);
  private readonly packageDomain = inject(PackageDomainService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly activeClient = inject(ACTIVE_CLIENT);

  selectedLanguage = signal<string | null>(null);
  selectedNorm = signal<string | null>(null);
  jobProfiles = signal<Models.JobProfiles | null>(null);
  selectedJobProfile = signal<string | null>(null);
  isSubmitting = signal(false);
  apiError = signal<string | null>(null);

  readonly languageOrNormChanged = computed(() => {
    const current = this.report();
    return this.selectedLanguage() !== current.language || this.selectedNorm() !== current.norm;
  });

  isPackage(r: Models.Report | Models.Package): r is Models.Package {
    return (r as Models.Package).reports !== undefined ? true : false;
  }

  ngOnInit(): void {
    const current = this.report();
    this.selectedLanguage.set(current.language);
    this.selectedNorm.set(current.norm);

    if (current.jobProfile) {
      this.selectedJobProfile.set(current.jobProfile);
      this.reportDomain.loadReportJobProfiles(current.id).subscribe({
        next: (profiles) => this.jobProfiles.set(profiles),
        error: () => {
          const message = 'Failed to load job profiles.';
          this.apiError.set(message);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
    }
  }

  onLanguageChange(language: string) {
    this.selectedLanguage.set(language);
  }

  onNormChange(norm: string) {
    this.selectedNorm.set(norm);
  }

  save(updateGenerated = false) {
    if (this.isSubmitting()) {
      return;
    }
    const current = this.report();

    const language = this.selectedLanguage() ?? current.language;
    const norm = this.selectedNorm() ?? current.norm;
    const jobProfile =
      current.jobProfile !== null ? (this.selectedJobProfile() ?? current.jobProfile) : null;

    const updated: Models.Report | Models.Package = {
      ...current,
      language,
      norm,
      jobProfile,
    } as Models.Report | Models.Package;

    const currentProject = this.projectDomain.currentProject();
    const projectId = currentProject?.id ?? this.getRouteParam('projectId');
    const participantId = this.getRouteParam('participantId');

    if (!projectId) {
      const message = 'Missing project id.';
      this.apiError.set(message);
      this.snackBar.open(message, 'Close', { duration: 5000 });
      return;
    }

    let op$;

    if (this.isPackage(updated) && participantId) {
      const clientId = this.activeClient;

      if (!clientId) {
        const message = 'Missing client id.';
        this.apiError.set(message);
        this.snackBar.open(message, 'Close', { duration: 5000 });
        return;
      }

      op$ = this.packageDomain.updateParticipantPackageAndGenerate(
        clientId,
        projectId,
        participantId,
        updated as Models.Package,
      );
    } else if (this.isPackage(updated)) {
      op$ = this.packageDomain.updateProjectPackage(
        projectId,
        updated as Models.Package,
        updateGenerated,
      );
    } else if (participantId) {
      const clientId = this.activeClient;

      if (!clientId) {
        const message = 'Missing client id.';
        this.apiError.set(message);
        this.snackBar.open(message, 'Close', { duration: 5000 });
        return;
      }

      op$ = this.reportDomain.updateParticipantReportAndGenerate(
        clientId,
        projectId,
        participantId,
        updated as Models.Report,
      );
    } else {
      op$ = this.reportDomain.updateProjectReport(
        projectId,
        updated as Models.Report,
        updateGenerated,
      );
    }

    this.isSubmitting.set(true);
    this.apiError.set(null);

    op$
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
        }),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['../..'], {
            relativeTo: this.route,
            state: { showLoading: false },
          });
        },
        error: (err) => {
          const message = err?.error?.message || err?.message || 'Failed to save settings.';
          this.apiError.set(message);
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
  }

  private getRouteParam(name: string): string | null {
    let route: ActivatedRoute | null = this.route;
    while (route) {
      const value = route.snapshot.paramMap.get(name);
      if (value) {
        return value;
      }
      route = route.parent;
    }
    return null;
  }
}
